import { useEffect, useState } from 'react';
import { api, ManualReadingRecord } from '../../api/client';
import { parseDbTimestamp } from '../../lib/parseDbTimestamp';
import { Badge } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';
import { DownloadOriginalButton } from '../../components/DownloadOriginalButton';

function readingsOf(row: ManualReadingRecord): Record<string, number> {
  const v = row.values_json;
  if (typeof v === 'string') {
    try { return JSON.parse(v) as Record<string, number>; } catch { return {}; }
  }
  return v || {};
}

// Routed through ui/Badge. The four values are `manual_reading_record.status`
// (workers/billing-api/migrations/0017_field_tools.sql:59 —
// CHECK (status IN ('pending_approval','committed','rejected','failed'))).
//
// 'failed' was `neutral` here until 2026-07-30. That grey was not a decision:
// it is the mechanical hue-preserving carry-over of the original hand-rolled
// `bg-gray-200 text-gray-700` (commit 85ade2b, restyled in a9022c9), and grey
// is the wrong signal for it. `failed` is the *system* failing to land the
// technician's reading in OpenRemote — their submitted work did not arrive —
// whereas `rejected` is a human admin declining it, with a reason the row
// renders underneath. A neutral chip reads as "archived / nothing to do",
// which is precisely what a lost reading is not.
//
// `danger` is what this app already uses for a system-side failure:
// BillRuns.tsx and BillVerification.tsx both map `error -> danger`, and both
// reserve `orange` for the *human rejection* case ("error whose message
// starts with `Rejected:`"). So `danger` is the in-repo-consistent choice,
// not a new one. It does mean `failed` and `rejected` share a hue on this
// screen — they stay distinguishable by their label text and by the red
// "Rejected: <reason>" line that only rejected rows carry. (Note the
// inversion vs. the app-wide precedent: elsewhere the human rejection is the
// orange one. Realigning `rejected` is out of scope for this pass.)
//
// Reachability, for whoever reads this next: billing-api never writes
// 'failed' to manual_reading_record today — insert is always
// 'pending_approval' (index.ts:8341), approve writes 'committed', reject
// writes 'rejected', and an OR write that fails at approval records `error`
// + `or_response_summary` and leaves the status alone (index.ts:8540-8545).
// The status is nonetheless live in the schema and the SPA already treats it
// as an error elsewhere (ReadingPage.tsx:185 surfaces `status === 'failed'`
// as "Submission failed: …"), so this is the tone it must have if a writer
// ever appears — not a shade to leave wrong because it is unreachable.
const STATUS_TONE: Record<string, BadgeTone> = {
  pending_approval: 'warning',
  committed: 'success',
  rejected: 'danger',
  failed: 'danger',
};

export default function MySubmissionsPage() {
  const [rows, setRows] = useState<ManualReadingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const result = await api.getMyManualReadings();
        setRows(result.data);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="bg-surface-raised rounded-xl shadow p-6 text-content-muted">Loading...</div>;
  }
  if (error) {
    // text-red-700 alone is 2.34:1 against surface-raised dark — fails AA.
    // dark:text-red-400 = 5.48:1, computed by hand. This is an error message
    // the technician must be able to read, not decorative chrome.
    return <div className="bg-surface-raised rounded-xl shadow p-6 text-red-700 dark:text-red-400">{error}</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="bg-surface-raised rounded-xl shadow p-12 text-center text-content-muted">
        You haven't submitted any readings yet.
      </div>
    );
  }

  return (
    // Not wrapped in <Card>: this shell is bg-white/rounded-xl/shadow with no
    // border-gray-* — it doesn't match the Card-adoption trigger pattern
    // (bg-white rounded-* shadow-* border-gray-*), and Card's own
    // `border border-border-token` would add a border that wasn't there
    // before (a light-mode appearance change). Token-substituted in place.
    <div className="bg-surface-raised rounded-xl shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-border-token">
        <h2 className="text-lg font-semibold text-content">My submissions</h2>
        <p className="text-sm text-content-muted mt-0.5">Most recent first, up to 100.</p>
      </div>
      <ul className="divide-y divide-border-token">
        {rows.map(row => {
          const readings = readingsOf(row);
          return (
            <li key={row.id} className="px-6 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-content">
                    {row.meter_serial}
                    <span className="ml-2 text-xs font-normal text-content-muted">{row.source}</span>
                  </p>
                  {/* Promoted to text-content (not text-content-muted): this
                      line carries the actual submitted reading values
                      (`k=v` pairs), not just the timestamp — a technician
                      checking their own submission history needs to read the
                      values, not just know something was submitted. */}
                  <p className="text-xs text-content mt-0.5">
                    {parseDbTimestamp(row.captured_at)?.toLocaleString() ?? row.captured_at} —{' '}
                    {Object.entries(readings).map(([k, v]) => `${k}=${v}`).join(', ') || 'no values'}
                  </p>
                  {row.rejected_reason && (
                    // text-red-700 alone is 2.34:1 against surface-raised dark
                    // — fails. dark:text-red-400 = 5.48:1. This is literally
                    // an error message (why the submission was rejected).
                    <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">Rejected: {row.rejected_reason}</p>
                  )}
                  {row.image_r2_key && (
                    <div className="mt-1.5">
                      <DownloadOriginalButton kind="reading" id={row.id} />
                    </div>
                  )}
                </div>
                <Badge tone={STATUS_TONE[row.status] ?? 'neutral'}>
                  {row.status}
                </Badge>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
